import { useQuery } from '@tanstack/react-query';
import { addressesApi } from '~/api/addresses';

export const COUNTRIES_QUERY_KEY = 'countries';

/**
 * Fetches the list of supported countries for vendor addresses.
 *
 * The response is normalized to an array of `{ id, name, code }` objects and cached for 10 minutes.
 * Consumers should rely on this hook to map `countryOfOriginId` values to a human-readable name.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Enables the query when true.
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{id:number|string,name:string|null,code:string|null}>, Error>}
 */
export const useCountriesQuery = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: [COUNTRIES_QUERY_KEY],
        enabled,
        queryFn: async () => {
            const response = await addressesApi.getCountries(true, 'asc');
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch countries');
            }
            if (!Array.isArray(response?.data?.rows)) {
                throw new Error('Unexpected response: countries is not an array');
            }
            return response.data.rows;
        },
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
    });
};
